import React, { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AssetDownloadButtonProps {
  /** Triggers a browser download of the asset; may reject on failure. */
  onDownload: () => Promise<void>;
  /** Optional visual variant passed through to the underlying Button. */
  variant?: React.ComponentProps<typeof Button>["variant"];
  /** Optional size passed through to the underlying Button. */
  size?: React.ComponentProps<typeof Button>["size"];
  /** Optional extra classes for layout (e.g. alignment in a header). */
  className?: string;
}

/**
 * Download button with built-in busy + error state, shared by the
 * additional-asset views (the unsupported-asset fallback and the renderable tab
 * header) so the busy/error UX lives in one place.
 */
const AssetDownloadButton: React.FC<AssetDownloadButtonProps> = ({
  onDownload,
  variant,
  size,
  className,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    setError(false);
    try {
      await onDownload();
    } catch {
      setError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`flex flex-col items-center gap-2 ${className ?? ""}`}>
      <Button
        onClick={handleDownload}
        disabled={isDownloading}
        variant={variant}
        size={size}
        className="flex items-center gap-2"
      >
        {isDownloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        <span>Download</span>
      </Button>
      {error && (
        <p className="text-sm text-destructive">
          Download failed. Please try again.
        </p>
      )}
    </div>
  );
};

export default AssetDownloadButton;
